// src/components/workspace/NotesTab.js
import React, { useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import { NotebookPen } from "lucide-react";

/**
 * Notes are supplied by the caller rather than fetched here. This component
 * used to open its own listener on the same collection the workspace already
 * subscribes to, which meant two live queries per open workspace and two copies
 * of the same data that could disagree while writes settled.
 */
function NotesTab({ problemId, currentUser, notes = [] }) {
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState("");

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await addDoc(collection(db, "problems", problemId, "notes"), {
        content: newNote,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewNote("");
      toast.success("Note added!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to add note");
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editingContent.trim()) return;
    try {
      await updateDoc(doc(db, "problems", problemId, "notes", noteId), {
        content: editingContent,
        updatedAt: serverTimestamp(),
      });
      setEditingNoteId(null);
      setEditingContent("");
      toast.success("Note updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteDoc(doc(db, "problems", problemId, "notes", noteId));
      toast.success("Note deleted!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete note");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  return (
    <div className="flex flex-col bg-white rounded-lg border border-slate-200 max-h-[calc(100vh-250px)]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <NotebookPen className="w-4 h-4 text-slate-500" />
          Notes
          {notes.length > 0 && (
            <span className="ml-2 text-xs text-slate-500 font-normal">
              {notes.length} {notes.length === 1 ? "note" : "notes"}
            </span>
          )}
        </h2>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {notes.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <NotebookPen className="w-10 h-10 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-sm">
                No notes yet. Add your first collaborative note!
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="bg-white border border-slate-200 rounded-lg p-4"
              >
                {editingNoteId === note.id ? (
                  <div>
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                    />
                    <div className="flex items-center space-x-2 mt-2">
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingContent("");
                        }}
                        className="border border-slate-300 text-slate-700 px-4 py-1 rounded hover:bg-slate-50 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900 mb-1">
                          {note.createdByName}
                        </p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {note.content}
                        </p>
                      </div>
                      {note.createdBy === currentUser.uid && (
                        <div className="flex items-center space-x-2 ml-2">
                          <button
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditingContent(note.content);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-rose-600 hover:text-rose-800 text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">
                        {formatDate(note.createdAt)}
                      </span>
                      {note.updatedAt?.seconds !== note.createdAt?.seconds && (
                        <span className="text-xs text-slate-400 italic">
                          (edited)
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Note Form */}
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
        <div className="flex flex-col space-y-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Write a note..."
            rows={3}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <button
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            className="self-end bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotesTab;

